import React, { useState, useCallback } from 'react';
import { View, StyleSheet, KeyboardAvoidingView, Platform } from 'react-native';
import { TextInput, Button, Text, useTheme, Surface } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { SafeAreaView } from 'react-native-safe-area-context';
import { InfoBox } from '@/src/components/InfoBox';
import { requestPhoneVerificationCode, confirmPhoneVerificationCode } from './phoneVerificationService';
import type { ApiResponse } from '@/src/api/apiService';

interface PhoneVerificationRequestScreenProps {
  /** Current phone number (pre-filled) */
  phoneNumber: string;
  /** Callback when OTP is successfully sent */
  onOTPSent?: () => void;
  /** Callback when verification is complete (for pre-verified phones) */
  onVerificationComplete?: () => void;
  /** Whether the phone is already verified (skips request step) */
  isAlreadyVerified?: boolean;
  /** Test ID for testing */
  testID?: string;
}

export interface PhoneVerificationRequestScreenRef {
  /** Focus the phone input */
  focusPhoneInput: () => void;
}

export const PhoneVerificationRequestScreen: React.FC<PhoneVerificationRequestScreenProps> = ({
  phoneNumber: initialPhoneNumber,
  onOTPSent,
  onVerificationComplete,
  isAlreadyVerified = false,
  testID = 'phone-verification-request',
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber || '');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);

  // Format phone number as user types (basic formatting)
  const formatPhoneNumber = (value: string): string => {
    const cleaned = value.replace(/[^\d+]/g, '');
    if (cleaned.startsWith('+')) {
      const digits = cleaned.slice(1).replace(/\+/g, '').slice(0, 15);
      return `+${digits}`;
    }
    return cleaned.replace(/\+/g, '').slice(0, 15);
  };

  const handlePhoneChange = (text: string) => {
    const formatted = formatPhoneNumber(text);
    setPhoneNumber(formatted);
    setError(null);
  };

  const validatePhoneNumber = (): boolean => {
    if (!phoneNumber || phoneNumber.length < 8) {
      setError(t('phoneNumberInvalid', 'Phone number must be at least 8 digits'));
      return false;
    }
    if (phoneNumber.length > 15) {
      setError(t('phoneNumberTooLong', 'Phone number is too long'));
      return false;
    }
    return true;
  };

  const handleRequestCode = useCallback(async () => {
    if (!validatePhoneNumber()) return;
    
    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response: ApiResponse<{ message: string; cooldown_seconds?: number }> = 
        await requestPhoneVerificationCode(phoneNumber);

      if (response.ok) {
        setSuccessMessage(response.data?.message || t('verificationCodeSent', 'Verification code has been sent'));
        if (response.data?.cooldown_seconds) {
          setCooldownSeconds(response.data.cooldown_seconds);
        }
        onOTPSent?.();
      } else {
        const errorDetail = (response.data as any)?.detail || 
                           (response.data as any)?.phone_number?.[0] ||
                           t('verificationRequestFailed', 'Failed to send verification code');
        setError(errorDetail);
      }
    } catch (err) {
      setError(t('verificationRequestFailed', 'Failed to send verification code. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  }, [phoneNumber, t, onOTPSent]);

  // If already verified, show success and call callback
  React.useEffect(() => {
    if (isAlreadyVerified) {
      setSuccessMessage(t('phoneAlreadyVerified', 'Phone number is already verified'));
      onVerificationComplete?.();
    }
  }, [isAlreadyVerified, t, onVerificationComplete]);

  // Handle cooldown timer
  React.useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    if (cooldownSeconds > 0) {
      timer = setInterval(() => {
        setCooldownSeconds((prev) => Math.max(0, prev - 1));
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [cooldownSeconds]);

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.colors.background }]} testID={testID}>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardView}
      >
        <Surface style={styles.content} elevation={1}>
          <Text variant="headlineSmall" style={styles.title}>
            {t('phoneVerificationTitle', 'Verify Your Phone Number')}
          </Text>
          
          <Text variant="bodyMedium" style={styles.description}>
            {t('phoneVerificationDescription', 'Enter your phone number to receive a verification code via SMS.')}
          </Text>

          <View style={styles.inputContainer}>
            <TextInput
              label={t('phoneNumber', 'Phone Number')}
              mode="outlined"
              value={phoneNumber}
              onChangeText={handlePhoneChange}
              keyboardType="phone-pad"
              autoComplete="tel"
              textContentType="telephoneNumber"
              disabled={isLoading || isAlreadyVerified}
              error={!!error}
              style={styles.phoneInput}
              testID={`${testID}-phone-input`}
            />
            
            {error && (
              <Text variant="bodySmall" style={[styles.errorText, { color: theme.colors.error }]}>
                {error}
              </Text>
            )}
          </View>

          {successMessage && (
            <InfoBox
              variant="success"
              message={successMessage}
              dismissible
              onDismiss={() => setSuccessMessage(null)}
              testID={`${testID}-success`}
            />
          )}

          {!isAlreadyVerified && (
            <Button
              mode="contained"
              onPress={handleRequestCode}
              loading={isLoading}
              disabled={isLoading || !phoneNumber || cooldownSeconds > 0}
              style={styles.button}
              testID={`${testID}-request-button`}
            >
              {cooldownSeconds > 0 
                ? t('resendAvailableIn', 'Resend available in {{seconds}}s', { seconds: cooldownSeconds })
                : t('requestVerificationCode', 'Request Verification Code')}
            </Button>
          )}
        </Surface>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  keyboardView: {
    flex: 1,
  },
  content: {
    margin: 16,
    padding: 20,
    borderRadius: 12,
  },
  title: {
    fontWeight: '600',
    marginBottom: 8,
    textAlign: 'center',
  },
  description: {
    textAlign: 'center',
    marginBottom: 24,
    opacity: 0.7,
  },
  inputContainer: {
    marginBottom: 16,
  },
  phoneInput: {
    marginBottom: 4,
  },
  errorText: {
    marginTop: 4,
    marginLeft: 8,
  },
  button: {
    marginTop: 8,
  },
});

export default PhoneVerificationRequestScreen;
